import type { Result } from 'true-myth';

import type { BulkRemoveSkillRequest, SkillBulkRemovalReceipt } from '~/shared/data-contract/skill-bulk-removal';
import { skillBulkRemovalReceiptSchema } from '~/shared/data-contract/skill-bulk-removal';
import type {
    PaginatedSkillChangeEvents,
    SkillChangeEventsExportRequest,
    SkillChangeEventsExportResponse,
    SkillChangeEventsSort,
} from '~/shared/data-contract/skill-change-event';
import {
    paginatedSkillChangeEventsSchema,
    skillChangeEventsExportResponseSchema,
} from '~/shared/data-contract/skill-change-event';
import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { LanguageCode } from '~/shared/internationalization';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

export type SkillFetchers = {
    readonly bulkRemoveSkill: (
        args: Readonly<BulkRemoveSkillRequest> & { readonly skillId: string },
    ) => Promise<Result<SkillBulkRemovalReceipt, HttpFetcherError>>;
    readonly changeSkillFeedback: (args: {
        readonly description: string;
        readonly skillId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly fetchSkillChangeEvents: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly from?: string;
        readonly page: number;
        readonly size: number;
        readonly sort: SkillChangeEventsSort;
        readonly to?: string;
    }) => Promise<Result<PaginatedSkillChangeEvents, HttpFetcherError>>;
    readonly reportSkillFeedback: (args: {
        readonly description: string;
        readonly skillId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly requestSkillFeedback: (args: {
        readonly description: string;
        readonly title: string;
        readonly type?: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly requestSkillChangeEventsExport: (
        args: SkillChangeEventsExportRequest & { readonly acceptLanguage: LanguageCode },
    ) => Promise<Result<SkillChangeEventsExportResponse, HttpFetcherError>>;
};

export type FeedbackFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createSkillFetchers = (dependencies: FeedbackFetchersDependencies): SkillFetchers => {
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = dependencies;

    const requestSkillFeedback: SkillFetchers['requestSkillFeedback'] = async (payload) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/skills/feedback/request-skill`,
            payload,
        });
    };

    const changeSkillFeedback: SkillFetchers['changeSkillFeedback'] = async (payload) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/skills/feedback/change-skill`,
            payload,
        });
    };

    const reportSkillFeedback: SkillFetchers['reportSkillFeedback'] = async (payload) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/skills/feedback/report-skill`,
            payload,
        });
    };

    // The removal runs in one transaction, so the response is the finished receipt; there is no queued
    // job to attach to and therefore no conflict status to tolerate.
    const bulkRemoveSkill: SkillFetchers['bulkRemoveSkill'] = async ({
        architectureIds,
        employees,
        learningOpportunities,
        skillId,
        tasks,
    }) => {
        return validatingCobrainerApiFetcher.postJson(skillBulkRemovalReceiptSchema, {
            pathname: `/skills/${skillId}/bulk-remove`,
            payload: { architectureIds, employees, learningOpportunities, tasks },
        });
    };

    const fetchSkillChangeEvents: SkillFetchers['fetchSkillChangeEvents'] = async ({
        acceptLanguage,
        from,
        page,
        size,
        sort,
        to,
    }) => {
        return validatingCobrainerApiFetcher.get(paginatedSkillChangeEventsSchema, {
            pathname: '/skill-change-events',
            requestOptions: {
                headers: { 'Accept-Language': acceptLanguage },
                searchParams: {
                    page,
                    size,
                    sort,
                    ...(from === undefined ? {} : { from }),
                    ...(to === undefined ? {} : { to }),
                },
            },
        });
    };

    const requestSkillChangeEventsExport: SkillFetchers['requestSkillChangeEventsExport'] = async ({
        acceptLanguage,
        from,
        to,
    }) => {
        return validatingCobrainerApiFetcher.postJson(skillChangeEventsExportResponseSchema, {
            expectedStatusCodes: [409],
            pathname: '/skill-change-events/export',
            payload: { from, to },
            requestOptions: { headers: { 'Accept-Language': acceptLanguage } },
        });
    };

    return {
        bulkRemoveSkill,
        changeSkillFeedback,
        fetchSkillChangeEvents,
        reportSkillFeedback,
        requestSkillChangeEventsExport,
        requestSkillFeedback,
    };
};
